import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  ToothData,
  ToothLayer,
  ClinicalTreatment,
  TreatmentCategory,
  NGAPAct,
} from '../types/records';
import { DENTAL_STATES_CONFIG, getPrimaryTargetZone } from '../types/records';
import { recordsService } from '../services/recordsService';
import type { ChartSelectionData } from '../components/records/DentalChartPro';
import type { SelectionContext } from '../lib/ngapLogicEngine';
import {
  validateStateApplication,
  computeBridgeTeeth,
  normalizeSurfaces,
} from '../lib/dentalValidation';

/**
 * Build proper metadata for surface-based states so that the embed's
 * applySurfaceVisualization() can find the correct location arrays.
 * Each state expects a specific key: caries_locations, composite_fill_locations, etc.
 */
function buildSurfaceMetadata(stateCode: string, surfaces: string[]): Record<string, unknown> {
  // Normalize labial→buccal, palatal→lingual (#49)
  const normalized = normalizeSurfaces(surfaces);
  if (normalized.length === 0) return {};
  const metadataKeyMap: Record<string, string> = {
    CARIES: 'caries_locations',
    TEMP_FILL: 'temp_fill_locations',
    COMPOSITE: 'composite_fill_locations',
    AMALGAM: 'amalgam_fill_locations',
    SEALANT: 'sealant_locations',
    FRACTURE: 'fracture_locations',
    CERVICAL_CARIES: 'cervical_caries_locations',
    RECESSION: 'recession_locations',
  };
  const key = metadataKeyMap[stateCode];
  if (!key) return {};
  return { [key]: normalized };
}

export interface UseDentalRecordsOptions {
  patientId: string | null;
}

export interface UseDentalRecordsReturn {
  // Data
  teethData: Record<string, ToothData>;
  treatments: ClinicalTreatment[];
  categories: TreatmentCategory[];
  chartLoading: boolean;
  treatmentsLoading: boolean;
  catalogLoading: boolean;

  // Legacy selection (still used for single-tooth context)
  selectedTooth: number | null;
  selectedSurfaces: Set<string>;
  selectedAct: NGAPAct | null;

  // Pro chart selection (from iframe)
  chartSelection: ChartSelectionData | null;
  selectionContext: SelectionContext;
  sessionExtractions: number;

  // Actions
  selectTooth: (fdi: number) => void;
  selectSurface: (fdi: number, surface: string) => void;
  selectAct: (act: NGAPAct) => void;
  clearSelection: () => void;
  setChartSelection: (sel: ChartSelectionData) => void;

  // Apply state (the key feature: auto-record treatment)
  applyAct: () => Promise<void>;
  applyActLoading: boolean;

  // Quick apply: direct chart state update (for observations like CARIES, MISSING)
  quickApplyState: (stateCode: string) => Promise<void>;

  // Remove a layer from a tooth
  removeToothLayer: (toothNum: number, layerIndex: number) => Promise<void>;

  // Treatment actions
  completeTreatment: (id: string) => Promise<void>;
  deleteTreatment: (id: string) => Promise<void>;

  // Refresh
  refreshChart: () => Promise<void>;
  refreshTreatments: () => Promise<void>;

  // Error & Validation
  error: string | null;
  clearError: () => void;
  /** Validation message (shown as guidance toast) */
  validationMessage: string | null;
  validationGuidance: string | null;
  clearValidation: () => void;
  /** Last validation warnings (non-blocking) */
  validationWarnings: string[];
}

export function useDentalRecords({
  patientId,
}: UseDentalRecordsOptions): UseDentalRecordsReturn {
  // ── State ──
  const [teethData, setTeethData] = useState<Record<string, ToothData>>({});
  const [treatments, setTreatments] = useState<ClinicalTreatment[]>([]);
  const [categories, setCategories] = useState<TreatmentCategory[]>([]);

  const [chartLoading, setChartLoading] = useState(false);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [applyActLoading, setApplyActLoading] = useState(false);

  const [selectedTooth, setSelectedTooth] = useState<number | null>(null);
  const [selectedSurfaces, setSelectedSurfaces] = useState<Set<string>>(new Set());
  const [selectedAct, setSelectedAct] = useState<NGAPAct | null>(null);

  // Pro chart selection from iframe
  const [chartSelection, setChartSelectionRaw] = useState<ChartSelectionData | null>(null);
  const [sessionExtractions, setSessionExtractions] = useState(0);

  // Guard: when true, ignore empty selection events from the iframe
  // (the chart clears its selection during updateToothVisual after state apply)
  const isApplyingRef = useRef(false);
  const applyingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // #21: Reliable applying guard — set on, auto-clear after delay, single timer
  const startApplying = useCallback(() => {
    isApplyingRef.current = true;
    if (applyingTimerRef.current) clearTimeout(applyingTimerRef.current);
  }, []);
  const endApplying = useCallback((delayMs = 600) => {
    if (applyingTimerRef.current) clearTimeout(applyingTimerRef.current);
    applyingTimerRef.current = setTimeout(() => {
      isApplyingRef.current = false;
      applyingTimerRef.current = null;
    }, delayMs);
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [validationGuidance, setValidationGuidance] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  // Derive selectionContext for NGAP logic engine
  const selectionContext = useMemo<SelectionContext>(() => {
    if (chartSelection) {
      return {
        selectedTeeth: chartSelection.selectedTeeth || [],
        selectedSurfaces: chartSelection.selectedSurfaces || {},
        selectedParts: chartSelection.selectedParts || {},
        toothTypes: chartSelection.toothTypes || {},
      };
    }
    // Fallback: build from legacy single-tooth selection
    if (selectedTooth) {
      const surfaces: Record<string, string[]> = {};
      const surfList: string[] = [];
      for (const key of selectedSurfaces) {
        const [, surf] = key.split('-');
        if (surf) surfList.push(surf);
      }
      if (surfList.length > 0) {
        surfaces[String(selectedTooth)] = surfList;
      }
      return {
        selectedTeeth: [selectedTooth],
        selectedSurfaces: surfaces,
        selectedParts: {},
        toothTypes: {},
      };
    }
    return { selectedTeeth: [], selectedSurfaces: {}, selectedParts: {}, toothTypes: {} };
  }, [chartSelection, selectedTooth, selectedSurfaces]);

  // Handle chart selection updates from iframe
  const setChartSelection = useCallback((sel: ChartSelectionData) => {
    // Guard: ignore empty selection events that the iframe sends during
    // updateToothVisual after state apply / layer removal
    const isEmpty = sel.selectedTeeth.length === 0
      && Object.keys(sel.selectedSurfaces || {}).length === 0
      && Object.keys(sel.selectedParts || {}).length === 0;
    if (isApplyingRef.current && isEmpty) {
      return; // Suppress — the previous selection should remain active
    }
    setChartSelectionRaw(sel);
    // Sync the primary selectedTooth for backwards compat
    if (sel.selectedTeeth.length === 1) {
      setSelectedTooth(sel.selectedTeeth[0]);
    } else if (sel.selectedTeeth.length === 0) {
      // Try to derive tooth number from surfaces or parts
      const surfaceKeys = Object.keys(sel.selectedSurfaces || {});
      const partKeys = Object.keys(sel.selectedParts || {});
      const derivedTooth = surfaceKeys[0] ?? partKeys[0];
      if (derivedTooth) {
        setSelectedTooth(Number(derivedTooth));
      } else {
        setSelectedTooth(null);
      }
    }
  }, []);

  // ── Load NGAP catalog (once) ──
  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    recordsService
      .getNGAPCatalog()
      .then((cats) => {
        if (!cancelled) setCategories(cats);
      })
      .catch((err) => {
        if (!cancelled) console.error('[useDentalRecords] catalog error:', err);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Load dental chart when patient changes ──
  const refreshChart = useCallback(async () => {
    if (!patientId) {
      setTeethData({});
      return;
    }
    setChartLoading(true);
    try {
      const chart = await recordsService.getDentalChart(patientId);
      setTeethData(chart?.teeth_data || {});
    } catch (err) {
      console.error('[useDentalRecords] chart error:', err);
      setTeethData({});
    } finally {
      setChartLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    refreshChart();
  }, [refreshChart]);

  // ── Load treatments when patient changes ──
  const refreshTreatments = useCallback(async () => {
    if (!patientId) {
      setTreatments([]);
      return;
    }
    setTreatmentsLoading(true);
    try {
      const tx = await recordsService.getTreatments({ patient_id: patientId });
      setTreatments(tx);
    } catch (err) {
      console.error('[useDentalRecords] treatments error:', err);
      setTreatments([]);
    } finally {
      setTreatmentsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    refreshTreatments();
  }, [refreshTreatments]);

  // ── Selection ──
  const selectTooth = useCallback((fdi: number) => {
    setSelectedTooth((prev) => (prev === fdi ? null : fdi));
    setSelectedSurfaces(new Set());
  }, []);

  const selectSurface = useCallback((fdi: number, surface: string) => {
    const key = `${fdi}-${surface}`;
    setSelectedSurfaces((prev) => {
      const next = new Set(prev);
      // If clicking on a different tooth, clear all first
      const currentTooth = [...prev].length > 0
        ? parseInt([...prev][0].split('-')[0])
        : null;
      if (currentTooth !== null && currentTooth !== fdi) {
        next.clear();
      }
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    // Also select the tooth
    setSelectedTooth(fdi);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTooth(null);
    setSelectedSurfaces(new Set());
    setSelectedAct(null);
    setChartSelectionRaw(null);
    // Sync clear to iframe
    const chartPro = (window as any).__dentalChartPro;
    if (chartPro?.clearSelections) {
      chartPro.clearSelections();
    }
  }, []);

  const selectAct = useCallback((act: NGAPAct) => {
    setSelectedAct((prev) => (prev?.id === act.id ? null : act));
  }, []);

  // ── Apply Act (KEY FEATURE) ──
  // When user selects tooth/surfaces + NGAP act → create treatment + update chart
  const applyAct = useCallback(async () => {
    if (!patientId || !selectedAct) {
      setError('Sélectionnez une dent et un acte NGAP');
      return;
    }

    // Collect ALL selected teeth (multi-tooth support)
    const allTeeth: number[] = chartSelection?.selectedTeeth?.length
      ? [...chartSelection.selectedTeeth]
      : selectedTooth ? [selectedTooth] : [];

    if (allTeeth.length === 0) {
      setError('Sélectionnez au moins une dent');
      return;
    }

    setApplyActLoading(true);
    isApplyingRef.current = true;
    setError(null);

    try {
      const newTeethData = { ...teethData };
      const chartPro = (window as any).__dentalChartPro;
      const skippedTeeth: { tooth: number; reason: string }[] = [];

      // Apply the act to EACH selected tooth
      for (const toothNum of allTeeth) {
        // Determine surfaces for this specific tooth
        const surfaces: string[] = [];
        // Legacy surfaces (only for the primary selected tooth)
        if (toothNum === selectedTooth) {
          for (const key of selectedSurfaces) {
            const [_, surf] = key.split('-');
            if (surf) surfaces.push(surf);
          }
        }
        // Surfaces from iframe chart selection (per-tooth)
        if (chartSelection?.selectedSurfaces) {
          const toothSurfs = chartSelection.selectedSurfaces[String(toothNum)];
          if (toothSurfs) {
            for (const s of toothSurfs) {
              if (!surfaces.includes(s)) surfaces.push(s);
            }
          }
        }

        // ── VALIDATION per tooth ──
        if (selectedAct.chart_state_code) {
          const surfacesPerTooth: Record<string, string[]> = { [String(toothNum)]: surfaces };
          const validation = validateStateApplication(
            selectedAct.chart_state_code,
            [toothNum],
            teethData,
            surfacesPerTooth
          );
          if (!validation.valid) {
            skippedTeeth.push({ tooth: toothNum, reason: validation.error || 'Validation échouée' });
            continue; // Skip invalid teeth but continue with others
          }
        }

        // Create treatment for this tooth
        const treatmentData = {
          patient_id: patientId,
          ngap_act_id: selectedAct.id,
          tooth_number: toothNum,
          surfaces: surfaces.join(','),
          status: 'COMPLETED' as const,
          notes: `Applied from dental chart: ${selectedAct.code} - ${selectedAct.label}`,
        };

        await recordsService.createTreatment(treatmentData);

        // Update dental chart data for this tooth
        if (selectedAct.chart_state_code) {
          const toothKey = String(toothNum);
          const currentTooth: ToothData = newTeethData[toothKey]
            ? { ...newTeethData[toothKey], layers: [...(newTeethData[toothKey].layers || [])] }
            : { base_state: 'PRESENT', layers: [] };

          if (selectedAct.target_zone === 'WHOLE_TOOTH') {
            currentTooth.base_state = selectedAct.chart_state_code;
            const stateConfig = DENTAL_STATES_CONFIG[selectedAct.chart_state_code];
            if (stateConfig?.destructive) {
              currentTooth.layers = [];
            }
            if (selectedAct.chart_state_code === 'EXTRACT') {
              setSessionExtractions(prev => prev + 1);
            }
          } else {
            const existingLayerIdx = currentTooth.layers.findIndex(l => l.code === selectedAct.chart_state_code);
            if (existingLayerIdx >= 0) {
              const existingLyr = currentTooth.layers[existingLayerIdx];
              const mergedSurfaces = normalizeSurfaces([...existingLyr.surfaces, ...surfaces]);
              const mergedMetadata = buildSurfaceMetadata(selectedAct.chart_state_code!, mergedSurfaces);
              currentTooth.layers = currentTooth.layers.map((l, i) =>
                i === existingLayerIdx
                  ? { ...l, surfaces: mergedSurfaces, metadata: mergedMetadata, date_added: new Date().toISOString() }
                  : l
              );
            } else {
              const normalizedSurfs = normalizeSurfaces(surfaces);
              const newLayer: ToothLayer = {
                code: selectedAct.chart_state_code,
                target_zone: selectedAct.target_zone,
                surfaces: normalizedSurfs,
                date_added: new Date().toISOString(),
                metadata: buildSurfaceMetadata(selectedAct.chart_state_code, normalizedSurfs),
              };
              currentTooth.layers = [...currentTooth.layers, newLayer];
            }
          }

          newTeethData[toothKey] = currentTooth;

          // Send targeted apply-state to iframe for immediate visual
          if (chartPro?.applyState) {
            const actMetadata = buildSurfaceMetadata(selectedAct.chart_state_code, surfaces);
            chartPro.applyState(
              toothNum,
              selectedAct.chart_state_code,
              selectedAct.target_zone || 'CROWN_SURFACE',
              surfaces,
              '',
              actMetadata
            );
          }

          // Persist to backend
          try {
            await recordsService.updateTooth(patientId, toothNum, currentTooth);
          } catch (err) {
            console.warn(`[useDentalRecords] chart sync warning for tooth ${toothNum}:`, err);
          }
        }
      }

      setTeethData(newTeethData);

      // Show feedback for skipped teeth
      if (skippedTeeth.length > 0) {
        if (skippedTeeth.length === allTeeth.length) {
          // ALL teeth were skipped — show as error
          setError(`Impossible d'appliquer l'acte : ${skippedTeeth.map(s => `dent ${s.tooth} (${s.reason})`).join(', ')}`);
        } else {
          // Some were skipped — show as warning
          setValidationWarnings(skippedTeeth.map(s => `Dent ${s.tooth} ignorée : ${s.reason}`));
        }
      }

      // Refresh treatments list
      await refreshTreatments();

      // Clear ALL selection after applying (teeth auto-deselect)
      clearSelection();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Échec de l\'application du traitement';
      setError(msg);
      console.error('[useDentalRecords] applyAct error:', err);
    } finally {
      setApplyActLoading(false);
      endApplying();
    }
  }, [patientId, selectedAct, selectedTooth, selectedSurfaces, chartSelection, teethData, refreshTreatments, clearSelection]);

  // ── Helper: collect all teeth + surfaces from current selection ──
  const collectSelectionTeeth = useCallback((): { teeth: number[]; surfacesPerTooth: Record<string, string[]> } => {
    const teeth: number[] = [];
    const surfacesPerTooth: Record<string, string[]> = {};

    if (chartSelection) {
      if (chartSelection.selectedTeeth.length > 0) {
        teeth.push(...chartSelection.selectedTeeth);
      }
      for (const fdi of Object.keys(chartSelection.selectedSurfaces || {})) {
        const n = Number(fdi);
        if (n && !teeth.includes(n)) teeth.push(n);
        surfacesPerTooth[String(n)] = chartSelection.selectedSurfaces[fdi] ?? [];
      }
      for (const fdi of Object.keys(chartSelection.selectedParts || {})) {
        const n = Number(fdi);
        if (n && !teeth.includes(n)) teeth.push(n);
      }
    }
    if (teeth.length === 0 && selectedTooth) {
      teeth.push(selectedTooth);
    }
    // Also from legacy selectedSurfaces  
    for (const key of selectedSurfaces) {
      const [t, surf] = key.split('-');
      if (t && surf) {
        if (!surfacesPerTooth[t]) surfacesPerTooth[t] = [];
        if (!surfacesPerTooth[t].includes(surf)) surfacesPerTooth[t].push(surf);
      }
    }
    return { teeth, surfacesPerTooth };
  }, [chartSelection, selectedTooth, selectedSurfaces]);

  // ── Quick Apply State (direct chart update, no NGAP act) ──
  // For observations like CARIES, MISSING, IMPLANT, BRIDGE that don't have NGAP acts
  // Applies to ALL selected teeth, not just the first one
  // Now with SMART VALIDATION: prevents duplicates, incompatible combos, auto-removes conflicts
  const quickApplyState = useCallback(async (stateCode: string) => {
    if (!patientId) {
      setError('Aucun patient sélectionné');
      return;
    }

    const stateConfig = DENTAL_STATES_CONFIG[stateCode];
    if (!stateConfig) {
      setError(`État inconnu: ${stateCode}`);
      return;
    }

    // Collect ALL teeth to apply to (multi-tooth support)
    const { teeth: teethToApply, surfacesPerTooth } = collectSelectionTeeth();

    if (teethToApply.length === 0) {
      setError('Sélectionnez une dent d\'abord');
      return;
    }

    const primaryZone = getPrimaryTargetZone(stateCode);

    // ── BRIDGE special handling: fill gaps between selected teeth ──
    let finalTeeth = [...teethToApply];
    if (stateCode === 'BRIDGE') {
      finalTeeth = computeBridgeTeeth(teethToApply);
    }

    // ══════ SMART VALIDATION ══════
    const validation = validateStateApplication(stateCode, finalTeeth, teethData, surfacesPerTooth);

    // Clear previous validation
    setValidationMessage(null);
    setValidationGuidance(null);
    setValidationWarnings([]);

    if (!validation.valid) {
      // BLOCKED — show error + guidance
      setValidationMessage(validation.error ?? 'Action impossible');
      setValidationGuidance(validation.guidance ?? null);
      return; // Do NOT apply
    }

    // Show warnings if any (non-blocking)
    if (validation.warnings.length > 0) {
      setValidationWarnings(validation.warnings);
    }

    setApplyActLoading(true);
    startApplying();
    setError(null);

    try {
      // Deep-copy teeth data to avoid mutating React state (#4)
      const newTeethData: Record<string, ToothData> = {};
      for (const [k, v] of Object.entries(teethData)) {
        newTeethData[k] = { ...v, layers: [...(v.layers || [])] };
      }
      const chartPro = (window as any).__dentalChartPro;

      // ── Execute auto-actions FIRST (remove conflicting layers) ──
      // Sort by layerIndex descending to avoid index shift issues (#57)
      const sortedAutoActions = [...validation.autoActions].sort((a, b) => {
        if (a.toothNum !== b.toothNum) return 0;
        return (b.layerIndex ?? 0) - (a.layerIndex ?? 0);
      });
      for (const action of sortedAutoActions) {
        if (action.type === 'remove-layer' && action.layerIndex !== undefined) {
          const tk = String(action.toothNum);
          const tooth = newTeethData[tk];
          if (tooth && tooth.layers[action.layerIndex]) {
            tooth.layers = tooth.layers.filter((_, i) => i !== action.layerIndex);
            // Also tell the iframe to remove the layer visual
            if (chartPro?.sendMessage) {
              chartPro.sendMessage('remove-layer', { toothFDI: action.toothNum, layerIndex: action.layerIndex });
            }
          }
        }
      }

      let extractionCount = 0;

      for (const toothNum of finalTeeth) {
        const toothKey = String(toothNum);
        const currentTooth: ToothData = newTeethData[toothKey]
          ? { ...newTeethData[toothKey], layers: [...(newTeethData[toothKey].layers || [])] }
          : { base_state: 'PRESENT', layers: [] };

        // WHOLE_TOOTH states → set base_state (destructive: clears layers)
        if (primaryZone === 'WHOLE_TOOTH') {
          currentTooth.base_state = stateCode;
          if (stateConfig.destructive) {
            currentTooth.layers = [];
          }
          if (stateCode === 'EXTRACT') extractionCount++;
        } else {
          // Layer states → add or MERGE a layer with proper targetZone + surfaces + metadata
          const toothSurfaces: string[] = normalizeSurfaces(surfacesPerTooth[toothKey] ?? []);

          // ══ CONFLICT RESOLUTION: remove overlapping surfaces from conflicting layers ══
          const SURFACE_CONFLICT_GROUP = new Set([
            'CARIES', 'COMPOSITE', 'AMALGAM', 'TEMP_FILL', 'SEALANT', 'FRACTURE',
          ]);
          if (SURFACE_CONFLICT_GROUP.has(stateCode) && primaryZone === 'CROWN_SURFACE' && toothSurfaces.length > 0) {
            const newSurfSet = new Set(toothSurfaces.map(s => s.toLowerCase()));
            currentTooth.layers = currentTooth.layers
              .map(layer => {
                if (layer.code === stateCode) return layer;
                if (!SURFACE_CONFLICT_GROUP.has(layer.code)) return layer;
                if (layer.target_zone !== 'CROWN_SURFACE') return layer;
                const remaining = layer.surfaces.filter(s => !newSurfSet.has(s.toLowerCase()));
                if (remaining.length === layer.surfaces.length) return layer;
                if (remaining.length === 0) return null; // Mark for removal
                return { ...layer, surfaces: remaining, metadata: buildSurfaceMetadata(layer.code, remaining) };
              })
              .filter((l): l is ToothLayer => l !== null);
          }

          // Check if a layer with the SAME code already exists → MERGE surfaces into it
          const existingLayerIdx = currentTooth.layers.findIndex(l => l.code === stateCode);

          if (existingLayerIdx >= 0) {
            // MERGE: combine new surfaces into the existing layer (no duplicates, normalized)
            const existingLayer = currentTooth.layers[existingLayerIdx];
            const combinedSurfaces = normalizeSurfaces([...existingLayer.surfaces, ...toothSurfaces]);
            const mergedMetadata = buildSurfaceMetadata(stateCode, combinedSurfaces);

            currentTooth.layers = currentTooth.layers.map((l, i) =>
              i === existingLayerIdx
                ? { ...l, surfaces: combinedSurfaces, metadata: mergedMetadata, date_added: new Date().toISOString() }
                : l
            );
          } else {
            // NEW: no existing layer with this code — create a fresh one
            const metadata: Record<string, unknown> = buildSurfaceMetadata(stateCode, toothSurfaces);

            const newLayer: ToothLayer = {
              code: stateCode,
              target_zone: primaryZone,
              surfaces: [...new Set(toothSurfaces)],
              date_added: new Date().toISOString(),
              metadata,
            };

            currentTooth.layers = [...currentTooth.layers, newLayer];
          }
        }

        newTeethData[toothKey] = currentTooth;

        // Send targeted apply-state to iframe for immediate visual feedback
        if (chartPro?.applyState) {
          const iframeSurfaces: string[] = normalizeSurfaces(surfacesPerTooth[toothKey] ?? []);
          const metadata = buildSurfaceMetadata(stateCode, iframeSurfaces);
          chartPro.applyState(toothNum, stateCode, primaryZone, iframeSurfaces, '', metadata);
        }
      }

      // Update React state immediately for responsiveness
      setTeethData(newTeethData);

      // Track extraction count for NGAP pricing (#50)
      if (extractionCount > 0) {
        setSessionExtractions(prev => prev + extractionCount);
      }

      // Persist all teeth in parallel (#22) with rollback on failure (#5)
      const oldTeethData = { ...teethData }; // Snapshot before update for rollback
      const persistResults = await Promise.allSettled(
        finalTeeth.map(toothNum => {
          const toothKey = String(toothNum);
          return recordsService.updateTooth(patientId, toothNum, newTeethData[toothKey]);
        })
      );
      const failedTeeth = finalTeeth.filter((_, i) => persistResults[i].status === 'rejected');
      if (failedTeeth.length > 0) {
        console.warn(`[useDentalRecords] Failed to persist teeth: ${failedTeeth.join(', ')}`);
        if (failedTeeth.length === finalTeeth.length) {
          // ALL failed → full rollback to previous state
          setTeethData(oldTeethData);
          setError(`Échec de la sauvegarde pour toutes les dents (${failedTeeth.join(', ')}). Modifications annulées.`);
        } else {
          // Partial failure → rollback only failed teeth
          const partialRollback = { ...newTeethData };
          for (const toothNum of failedTeeth) {
            const tk = String(toothNum);
            if (oldTeethData[tk]) {
              partialRollback[tk] = oldTeethData[tk];
            } else {
              delete partialRollback[tk];
            }
          }
          setTeethData(partialRollback);
          setError(`Dent(s) ${failedTeeth.join(', ')} non sauvegardée(s). Les autres ont été enregistrées.`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Échec de l\'application de l\'état';
      setError(msg);
    } finally {
      setApplyActLoading(false);
      // Release the selection guard after a short delay
      endApplying();
      // Auto-deselect teeth after state application
      clearSelection();
    }
  }, [patientId, selectedTooth, chartSelection, selectedSurfaces, teethData, collectSelectionTeeth, clearSelection]);

  // ── Remove a layer from a tooth ──
  const removeToothLayerFn = useCallback(async (toothNum: number, layerIndex: number) => {
    if (!patientId) return;

    const toothKey = String(toothNum);
    const currentTooth = teethData[toothKey];
    if (!currentTooth || !currentTooth.layers[layerIndex]) return;

    setApplyActLoading(true);
    startApplying();
    setError(null);

    try {
      const newTeethData = { ...teethData };
      const updatedTooth: ToothData = {
        ...currentTooth,
        layers: currentTooth.layers.filter((_, i) => i !== layerIndex),
      };
      newTeethData[toothKey] = updatedTooth;
      setTeethData(newTeethData);

      // Send remove-layer to iframe
      const chartPro = (window as any).__dentalChartPro;
      if (chartPro?.sendMessage) {
        chartPro.sendMessage('remove-layer', { toothFDI: toothNum, layerIndex });
      }

      // Persist to backend
      try {
        await recordsService.updateTooth(patientId, toothNum, updatedTooth);
      } catch (err) {
        console.warn('[useDentalRecords] remove layer sync warning:', err);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Échec de la suppression de la couche';
      setError(msg);
    } finally {
      setApplyActLoading(false);
      endApplying();
    }
  }, [patientId, teethData]);

  // ── Treatment actions ──
  const completeTreatment = useCallback(
    async (id: string) => {
      try {
        await recordsService.completeTreatment(id);
        await refreshTreatments();
        await refreshChart(); // Chart may have updated from backend sync
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Échec de la complétion du traitement';
        setError(msg);
      }
    },
    [refreshTreatments, refreshChart]
  );

  const deleteTreatment = useCallback(
    async (id: string) => {
      try {
        await recordsService.deleteTreatment(id);
        await refreshTreatments();
        await refreshChart(); // Sync chart state after treatment deletion (#52)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Échec de la suppression du traitement';
        setError(msg);
      }
    },
    [refreshTreatments, refreshChart]
  );

  const clearError = useCallback(() => setError(null), []);
  const clearValidation = useCallback(() => {
    setValidationMessage(null);
    setValidationGuidance(null);
    setValidationWarnings([]);
  }, []);

  return {
    teethData,
    treatments,
    categories,
    chartLoading,
    treatmentsLoading,
    catalogLoading,
    selectedTooth,
    selectedSurfaces,
    selectedAct,
    chartSelection,
    selectionContext,
    sessionExtractions,
    selectTooth,
    selectSurface,
    selectAct,
    clearSelection,
    setChartSelection,
    applyAct,
    applyActLoading,
    quickApplyState,
    removeToothLayer: removeToothLayerFn,
    completeTreatment,
    deleteTreatment,
    refreshChart,
    refreshTreatments,
    error,
    clearError,
    validationMessage,
    validationGuidance,
    clearValidation,
    validationWarnings,
  };
}
